import React, { useState } from 'react';
import { X, UserCheck, UserX, Lock, Crown, Users } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';

const JoinInvitationModal = ({ isOpen, onClose, room, currentUser, onResponse }) => {
  const [loading, setLoading] = useState(false);
  const { colors } = useTheme();

  if (!isOpen || !room) return null;

  const handleAcceptInvitation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/rooms/${room.id}/accept-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (response.ok) {
        onResponse({
          success: true,
          action: 'accepted',
          message: data.message || 'Invitation accepted! Welcome to the room.',
          room: data.room
        });
        onClose();
      } else {
        onResponse({
          success: false,
          action: 'error',
          message: data.error || 'Failed to accept invitation'
        });
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      onResponse({
        success: false,
        action: 'error',
        message: 'Failed to accept invitation'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineInvitation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/rooms/${room.id}/decline-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (response.ok) {
        onResponse({
          success: true,
          action: 'declined',
          message: data.message || 'Invitation declined.',
          room: data.room
        });
        onClose();
      } else {
        onResponse({
          success: false,
          action: 'error',
          message: data.error || 'Failed to decline invitation'
        });
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      onResponse({
        success: false,
        action: 'error',
        message: 'Failed to decline invitation'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '500px',
        width: '100%',
        border: '1px solid #333',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Lock size={20} style={{ color: '#8b5cf6' }} />
            Private Room Invitation
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Room Info */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: '#e5e5e5'
          }}>
            {room.title}
          </h3>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <img
              src={room.host?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${room.host?.username}`}
              alt={room.host?.username}
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%'
              }}
            />
            <span style={{ fontSize: '14px', color: '#888' }}>
              Hosted by {room.host?.username}
            </span>
            <Crown size={12} style={{ color: '#fbbf24' }} />
          </div>

          {room.description && (
            <p style={{
              margin: '8px 0',
              fontSize: '14px',
              color: '#ccc',
              lineHeight: '1.4'
            }}>
              {room.description}
            </p>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginTop: '12px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: '#888'
            }}>
              <Users size={12} style={{ color: '#00d4aa' }} />
              {room.participant_count || 0}/{room.max_participants} members
            </div>
            
            {room.strain && (
              <div style={{
                padding: '2px 6px',
                background: '#10b981',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '500',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                🌿 {room.strain}
              </div>
            )}

            <div style={{
              padding: '2px 6px',
              background: room.is_live ? '#00d4aa' : '#666',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '600',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'white',
                animation: room.is_live ? 'pulse 2s infinite' : 'none'
              }} />
              {room.is_live ? 'LIVE' : 'OFFLINE'}
            </div>
          </div>
        </div>

        {/* Invitation Message */}
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '24px'
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#d1d5db',
            textAlign: 'center'
          }}>
            You've been invited to join this private sesh room! 
            Would you like to accept and become a member?
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={handleDeclineInvitation}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: 'transparent',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <UserX size={16} />
            {loading ? 'Processing...' : 'Decline'}
          </button>

          <button
            onClick={handleAcceptInvitation}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: '#8b5cf6',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <UserCheck size={16} />
            {loading ? 'Processing...' : 'Accept & Join'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinInvitationModal;